import React, { useEffect, useState } from 'react';
import { BadgeCheckIcons, ChevronDownIcons, FilterIcons, SearchIcons, XIcons } from '../../../icons/icons';
import { Button, Drawer } from '../../../ui';
import { CandibankJobApplicantRequest, JobApplicantSearchRequest, Skill } from '../../../utils/types/JobOpenings';
import SkillWeighting from '../../job_openings/create/ApplicantAnalysis/UI/SkillWeighting';

export interface FilterOptions {
    skills: string[];
    locations: string[];
    experienceLevels: string[];
    status: string[];
    matchRange: [number, number];
    availability: string[];
    sortBy: string;
    sortOrder: 'asc' | 'desc';
}

interface FilterDrawerProps {
    isOpen: boolean,
    onClose: () => void,
    weightingEnabled: boolean,
    setWeightingEnabled: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    extractedSkill: Skill[],
    setExtractedSkill: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    similarToggle: boolean,
    setSimilarToggle: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    extractedJobTitles: Skill[],
    setExtractedJobTitles: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    similarJobTitle: boolean,
    setSimilarJobTitle: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    extractedBehavioralScore: Skill[],
    setExtractedBehavioralScore: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    similarBehavior: boolean,
    setSimilarBehavior: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    keywordToggle: boolean,
    setKeywordToggle: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    keywordWeightage: boolean,
    setKeywordWeightage: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    extractedKeywords: Skill[],
    setExtractedKeywords: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    searchObject: CandibankJobApplicantRequest,
    setSearchObject: (value: (((prevState: CandibankJobApplicantRequest) => CandibankJobApplicantRequest) | CandibankJobApplicantRequest)) => void,
    genderList: string[],
    setGenderList: (
        value: ((prevState: string[]) => string[]) | string[]
    ) => void,
    minMaxAge: number[],
    setMinMaxAge: (minMax: number[]) => void,
    ageToggle: boolean,
    setAgeToggle: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    setUseSkillPercentage: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    statusList: string[],
    loading: boolean,
    setLoading: (value: (((prevState: boolean) => boolean) | boolean)) => void
}

export const GraphFilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  extractedSkill,
  setExtractedSkill,
  similarToggle,
  setSimilarToggle,
  weightingEnabled,
  setWeightingEnabled,
  extractedJobTitles,
  setExtractedJobTitles,
  similarJobTitle,
  setSimilarJobTitle,
  extractedBehavioralScore,
  setExtractedBehavioralScore,
  similarBehavior,
  setSimilarBehavior,
  keywordToggle,
  setKeywordToggle,
  keywordWeightage,
  setKeywordWeightage,
  extractedKeywords,
  setExtractedKeywords,
  searchObject,
  setSearchObject,
  genderList,
  setGenderList,
  minMaxAge,
  setMinMaxAge,
  ageToggle,
  setAgeToggle,
  setUseSkillPercentage,
  statusList,
  loading,
  setLoading,
}) => {

  const handleApplyChange = async () => {

    const jobTitles = extractedJobTitles.map(item => item.skill);
    const behavioralScore = extractedBehavioralScore.map(item => item.skill);
    const keywords = extractedKeywords.map(({ skill, weight }) => ({
      keyword: skill,
      weight: weight,
    }));
    const cleanedSkills = extractedSkill.map(({ skill, weight }) => ({
      skill,
      weight,
    }));

    setUseSkillPercentage(weightingEnabled);

    const data = {
      ...searchObject,
      age: {
        ...searchObject.age,
        minAge: minMaxAge[0],
        maxAge: minMaxAge[1],
        includeUnknown: ageToggle,
      },
      additionalSkills: {
        skillWeightage: weightingEnabled,
        similar: similarToggle,
        skills: cleanedSkills.length === 0 ? [] : cleanedSkills,
      },
      jobTitle: {
        similar: similarJobTitle,
        jobTitles: jobTitles.length === 0 ? [] : jobTitles,
      },
      keywords: {
        keywordWeightage: keywordWeightage,
        similar: keywordToggle,
        keywords: keywords.length === 0 ? [] : keywords,
      },
      behavioralAttributes: {
        similar: similarBehavior,
        behavioralAttributes: behavioralScore.length === 0 ? [] : behavioralScore,
      },
      gender: genderList,
      applicantStatus: statusList,
    };

    setSearchObject(data);
  };

  return (
    <Drawer
        isOpen={isOpen}
        onClose={onClose}
        footerContent={
          <div className='flex justify-end gap-2.5 w-full'>
            <Button label='Apply' onClick={() => handleApplyChange()} variant='primary'/>
            <Button label='Close' onClick={onClose} variant='secondary' disabled={loading}/>
          </div>
        }
        title='Apply Filters'
        width='w-full md:w-[740px] lg:w-[800px]'
    >
      <div className='space-y-4'>
        <section className='bg-white rounded-xl border border-gray-200 shadow-xs p-4 sm:p-5'>
          <div className='flex items-center gap-2 mb-3.5'>
            <span className='flex items-center justify-center w-6 h-6 rounded-md bg-emerald-50 text-emerald-700 text-xs font-semibold'>
              📊
            </span>
            <div>
              <h2 className='text-xs font-bold uppercase tracking-wider text-gray-800'>Graph Axis Filters</h2>
              <p className='text-[11px] text-gray-400'>Configure 3D axes, importance weighting, and term matching</p>
            </div>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-3.5 items-stretch'>
            { /* Skill(s) */ }
            <div className={`h-full flex flex-col ${loading ? 'animate-pulse' : ''}`}>
              <SkillWeighting
                  title={'X: Skill(s)'}
                  attributeName={'Skill'}
                  isWeightRequired={true}
                  extractedSkill={extractedSkill}
                  setExtractedSkill={setExtractedSkill}
                  weightingEnabled={weightingEnabled}
                  setWeightingEnabled={setWeightingEnabled}
                  similarEnabled={similarToggle}
                  setSimilarEnabled={setSimilarToggle}
              />
            </div>
            { /* Experience Job Titles */ }
            <div className={`h-full flex flex-col ${loading ? 'animate-pulse' : ''}`}>
              <SkillWeighting
                  title={'Y: Experience Job Title(s)'}
                  attributeName={'job title'}
                  extractedSkill={extractedJobTitles}
                  setExtractedSkill={setExtractedJobTitles}
                  weightingEnabled={weightingEnabled}
                  setWeightingEnabled={setWeightingEnabled}
                  similarEnabled={similarJobTitle}
                  setSimilarEnabled={setSimilarJobTitle}
              />
            </div>
            { /* Separator */ }
            <div className='col-span-full flex items-center gap-2.5 my-1'>
              <div className='flex-grow h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent'></div>
              <span className='inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-50 px-3 py-1 rounded-full border border-gray-200/80 shadow-2xs'>
                <svg className='w-3 h-3 text-emerald-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5' />
                </svg>
                3D Graph Extended Axes
              </span>
              <div className='flex-grow h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent'></div>
            </div>

            { /* Behavioral Attributes */ }
            <div className={`h-full flex flex-col ${loading ? 'animate-pulse' : ''}`}>
              <SkillWeighting
                  title={'Z: Behavioral Attribute(s)'}
                  attributeName={'attribute'}
                  extractedSkill={extractedBehavioralScore}
                  setExtractedSkill={setExtractedBehavioralScore}
                  weightingEnabled={weightingEnabled}
                  setWeightingEnabled={setWeightingEnabled}
                  similarEnabled={similarBehavior}
                  setSimilarEnabled={setSimilarBehavior}
              />
            </div>

            { /* Keywords Counter */ }
            <div className={`h-full flex flex-col ${loading ? 'animate-pulse' : ''}`}>
              <SkillWeighting
                  title={'Keyword Counter'}
                  attributeName={'keyword'}
                  isWeightRequired={true}
                  extractedSkill={extractedKeywords}
                  setExtractedSkill={setExtractedKeywords}
                  weightingEnabled={keywordWeightage}
                  setWeightingEnabled={setKeywordWeightage}
                  similarEnabled={keywordToggle}
                  setSimilarEnabled={setKeywordToggle}
              />
            </div>
          </div>
        </section>
      </div>
    </Drawer>
  );
};
